<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_CURRENCY_RATE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="currencyRateRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="currencyRateList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(averageRate)="data">
        {{ toBalance(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_CURRENCY_RATE')"
              @click="onSelectCurrencyRateItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCurrencyRateItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="currencyRateRules">
        <b-form>
          <b-row class="match-height">

            <!-- Currency Code -->
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="currencyRateFormData.currencyCode"
                :label="$t('Currency Code')"
                :placeholder="$t('Select')"
                :options="currencyCodeOptions"
                :reduce="option => option"
                :error-name="$t('Currency Code')"
                rules="required"
                item="label"
                for="currency-code"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Currency Code -->
          </b-row>

          <b-row>
            <!-- Buy Rate -->
            <b-col md="6">
              <InputNumberField
                id="buy-rate"
                v-model="currencyRateFormData.buyRate"
                :label="$t('Buy Rate')"
                :placeholder="$t('Enter buy rate')"
                type="number"
                :error-name="$t('Buy Rate')"
                rules="required"
                :readonly="isReadonly"
                @input="addBuyRate"
              />
            </b-col>
            <!-- Buy Rate -->

            <!-- Sell Rate -->
            <b-col md="6">
              <InputNumberField
                id="sell-rate"
                v-model="currencyRateFormData.sellRate"
                :label="$t('Sell Rate')"
                :placeholder="$t('Enter sell rate')"
                type="number"
                :error-name="$t('Sell Rate')"
                rules="required"
                :readonly="isReadonly"
                @input="addSellRate"
              />
            </b-col>
            <!-- Sell Rate -->

            <!-- Average Rate -->
            <b-col md="6">
              <InputNumberField
                id="average-rate"
                v-model="avarageRate"
                :label="$t('Average Rate')"
                :placeholder="$t('Enter average rate')"
                type="number"
                readonly
              />
            </b-col>
            <!-- Average Rate -->

          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCurrencyRates"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency Code')"
            :placeholder="$t('Select')"
            item="label"
            :options="currencyCodeOptions"
            :reduce="option => option"
          />
        </b-col>

        <!-- Start Date-->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :min-date="new Date()"
            :label="$t('Financial Date (From)')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  Start Date -->

        <!-- End Date-->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :min-date="new Date()"
            :label="$t('Financial Date (To)')"
            :config="datePickerConfig"
            :placeholder="$t('Select Date')"
          />
        </b-col>
        <!--  End Date -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdownItem, BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import SelectField from '../../../forms/SelectField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { toKobo, toBalance } from '../../../../helpers/formatAmount'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BSpinner,
    InputNumberField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    SelectField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Button,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'name',
      required,
      toBalance,
      toDateWithYear,
      modalTitle: this.$t('Create Currency Rate'),
      sortDesc: false,
      showSpinner: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'baseCurrencyCode', sortable: true, label: 'Base Currency' },
        { key: 'currencyCode', sortable: true, label: 'Foreign Currency' },
        { key: 'buyRate', sortable: true, label: 'Buy Rate' },
        { key: 'sellRate', sortable: true, label: 'Sell Rate' },
        { key: 'averageRate', sortable: true, label: 'Average Rate' },
        { key: 'actions', label: 'Actions' },
      ],
      currencyRateFormData: {
        currencyCode: '',
        buyRate: '',
        sellRate: '',
        id: '',
      },
      avarageRate: '',
      filter: {
        currencyCode: '',
        startDate: '',
        endDate: '',
      },
      currencyRateList: [],
      currencyCodeOptions: [],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'd/m/Y',
      },
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCurrencyRates()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCurrencyRates()
    },
  },
  mounted() {
    this.fetchCurrencyRates()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getCurrencyRates: 'operations/getCurrencyRates',
      updateCurrencyRate: 'operations/updateCurrencyRateAction',
      createCurrencyRate: 'operations/createCurrencyRateAction',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),
    addBuyRate(value) {
      this.avarageRate = ((this.currencyRateFormData.sellRate + value) / 2)
    },
    addSellRate(value) {
      this.avarageRate = ((this.currencyRateFormData.buyRate + value) / 2)
    },
    async fetchCurrencyRates() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCurrencyRates({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: operationPermissions.VIEW_CURRENCY_RATE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.currencyRateList = data.data.map(items => ({
            ...items,
            buyRate: items.buyRate ? toBalance(items.buyRate) : 0,
            sellRate: items.sellRate ? toBalance(items.sellRate) : 0,
          }))
          this.totalCount = data.recordCount
        } else {
          this.currencyRateList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: operationPermissions.VIEW_CURRENCY_RATE })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    async onSelectCurrencyRateItem(items, activity, val) {
      this.currencyRateFormData.currencyCode = items.currencyCode
      this.currencyRateFormData.buyRate = items.buyRate
      this.currencyRateFormData.sellRate = items.sellRate
      this.avarageRate = ((items.buyRate + items.sellRate) / 2)
      this.currencyRateFormData.id = items.id
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Currency Rate') : this.$t('Currency Rate')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async currencyRateAction() {
      const yourRequest = this.$t('Your request to')
      try {
        this.isLoading = true
        const formData = {
          ...this.currencyRateFormData,
          buyRate: this.currencyRateFormData.buyRate ? toKobo(this.currencyRateFormData.buyRate) : '',
          sellRate: this.currencyRateFormData.sellRate ? toKobo(this.currencyRateFormData.sellRate) : '',
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createCurrencyRate(formData)
          : await this.updateCurrencyRate(formData)
        const { status } = await response
        if (status === 200) {
          this.fetchCurrencyRates()
          this.hideModal('modal-lg')
          const successtext = this.$t('Currency Rate was successful')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${successtext}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Currency Rate failed. Reason:')
          this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.currencyRateRules.validate().then(async success => {
        if (success) {
          const areYourSure = this.$t('Are you sure you want to')
          const thisRate = this.$t('this Currency Rate?')
          this.confirm(this.currencyRateAction, { text: `${areYourSure} ${this.buttonLabel} ${thisRate}` })
        }
      })
    },
    resetModal() {
      this.currencyRateFormData.currencyCode = null
      this.currencyRateFormData.buyRate = null
      this.currencyRateFormData.sellRate = null
      this.currencyRateFormData.id = null
      this.isReadonly = false
      this.isLoading = false
      this.avarageRate = null
      this.modalTitle = this.$t('Create Currency Rate')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter.name = ''
      this.filter.code = ''
      this.filter.status = ''
      this.filter.operationStatus = ''
      this.filter.sortColumn = ''
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCurrencyRates()
    },
  },
}
</script>
